import axios from "axios";
import { API_BASE_URL, getBasicAuthHeader, isBasicConfigured } from "./config";
import { unwrapResponse } from "./unpack";

const jsonHeaders = { "Content-Type": "application/json" };
const protectedClient = axios.create({ baseURL: API_BASE_URL });

/**
 * @param {string} accountUrl
 * @param {string} pathWithoutQuery
 */
function withAccountUrl(pathWithoutQuery, accountUrl) {
  const joiner = pathWithoutQuery.includes("?") ? "&" : "?";
  return `${pathWithoutQuery}${joiner}account_url=${encodeURIComponent(accountUrl)}`;
}

/**
 * @param {string} accountUrl
 * @param {import("axios").Method} method
 * @param {string} path
 * @param {unknown} [data]
 * @param {import("axios").AxiosRequestConfig} [extra]
 */
function requestWithBasic(accountUrl, method, path, data, extra = {}) {
  if (!isBasicConfigured()) {
    return Promise.reject(
      new Error("Задайте REACT_APP_API_APP_ID и REACT_APP_API_APP_SECRET в окружении"),
    );
  }
  const auth = getBasicAuthHeader();
  const url = withAccountUrl(path, accountUrl);
  const m = String(method).toLowerCase();
  const cfg = {
    method: m,
    url,
    ...extra,
    headers: { ...jsonHeaders, ...extra?.headers, Authorization: auth },
  };
  if (m !== "get" && m !== "head" && data !== undefined) {
    cfg.data = data;
  }
  return protectedClient.request(cfg).then((r) => unwrapResponse(r.data));
}

/**
 * @param {string} accountUrl
 * @param {Record<string, unknown>} body
 */
export function postClient(accountUrl, body) {
  return requestWithBasic(accountUrl, "post", "/clients", body);
}

/**
 * @param {string} accountUrl
 */
export function getClientMe(accountUrl) {
  return requestWithBasic(accountUrl, "get", "/clients/me", undefined, {
    headers: jsonHeaders,
  });
}

/**
 * @param {string} accountUrl
 * @param {Record<string, unknown>} body
 */
export function postSpot(accountUrl, body) {
  return requestWithBasic(accountUrl, "post", "/spots", body);
}

/**
 * @param {string} accountUrl
 */
export function getSpots(accountUrl) {
  return requestWithBasic(accountUrl, "get", "/spots", undefined, {
    headers: jsonHeaders,
  });
}

/**
 * @param {string} accountUrl
 * @param {Record<string, unknown>} body
 */
export function postTablet(accountUrl, body) {
  return requestWithBasic(accountUrl, "post", "/tablets", body);
}

/**
 * @param {string} accountUrl
 */
export function getTablets(accountUrl) {
  return requestWithBasic(accountUrl, "get", "/tablets", undefined, {
    headers: jsonHeaders,
  });
}

/**
 * История изменений баланса клиента (чтение), с пагинацией.
 * @param {string} accountUrl
 * @param {{ page?: number; limit?: number }} [options] page с 1, limit по умолчанию 20, макс. 100
 * @returns {Promise<{ items?: unknown[]; meta?: { total: number; page: number; limit: number; totalPages: number } } | unknown>}
 */
export function getBalanceHistory(accountUrl, options = {}) {
  const page = Math.max(1, Math.floor(Number(options.page)) || 1);
  const rawLimit = Math.floor(Number(options.limit)) || 20;
  const limit = Math.min(100, Math.max(1, rawLimit));
  const path = `/balance-history?page=${page}&limit=${limit}`;
  return requestWithBasic(accountUrl, "get", path, undefined, {
    headers: jsonHeaders,
  });
}

/**
 * Создать транзакцию (оплата): в теле amount в минимальных единицах (×100 к сумам), tablet_id.
 * При успехе в теле ответа поле message — URL редиректа на оплату (Paycom и т.п.).
 * @param {string} accountUrl
 * @param {{ amount: number; tablet_id: string }} body
 */
export function postTransaction(accountUrl, body) {
  return requestWithBasic(accountUrl, "post", "/transactions", body, {
    headers: jsonHeaders,
  });
}

/**
 * Списать с баланса очередной платёж за подписку планшета.
 * @param {string} accountUrl
 * @param {string} tabletId Mongo `_id` планшета
 */
export function makeTabletPayment(accountUrl, tabletId) {
  const path = `/tablets/${encodeURIComponent(String(tabletId))}/make-payment`;
  return requestWithBasic(accountUrl, "patch", path, undefined, {
    headers: jsonHeaders,
  });
}

/**
 * Список отложенных UzQR-платежей клиента.
 * @param {string} accountUrl
 * @param {{ status?: string, tablet_id?: number, spot_id?: number, limit?: number }} [filter]
 */
export function getDeferredPayments(accountUrl, filter = {}) {
  const params = new URLSearchParams();
  if (filter.status) params.set("status", filter.status);
  if (filter.tablet_id !== undefined) params.set("tabletid", String(filter.tablet_id));
  if (filter.spot_id !== undefined) params.set("spotid", String(filter.spot_id));
  if (filter.limit !== undefined) params.set("limit", String(filter.limit));
  const query = params.toString();
  const path = query ? `/deferred-payments?${query}` : "/deferred-payments";
  return requestWithBasic(accountUrl, "get", path, undefined, {
    headers: jsonHeaders,
  });
}

/**
 * Изменить статус отложенного UzQR-платежа.
 * @param {string} accountUrl
 * @param {string} id
 * @param {{ status: string, transaction_id?: string }} body
 */
export function updateDeferredPaymentStatus(accountUrl, id, body) {
  const path = `/deferred-payments/${encodeURIComponent(id)}/status`;
  return requestWithBasic(accountUrl, "patch", path, body, {
    headers: jsonHeaders,
  });
}

/**
 * Удалить отложенный UzQR-платёж.
 * @param {string} accountUrl
 * @param {string} id
 */
export function deleteDeferredPayment(accountUrl, id) {
  const path = `/deferred-payments/${encodeURIComponent(id)}`;
  return requestWithBasic(accountUrl, "delete", path, undefined, {
    headers: jsonHeaders,
  });
}

export { isBasicConfigured };
